import type { CSSProperties, Ref, ComputedRef, WritableComputedRef } from 'vue';
import { computed, unref } from 'vue';
import useUser from './useUser';
import type { MaybeRef, RemovableRef } from '@vueuse/core';
import { useClipboardItems, useLocalStorage } from '@vueuse/core';
import { LocalStorageEntities } from '@/constants/LocalStorageEntities';
import simplexLogo from '../assets/faux-whitelabel/simplex-white.svg';
import qraftLogo from '../assets/faux-whitelabel/qraft.svg';
import wtwLogo from '../assets/faux-whitelabel/wtw.svg';
import atlanticHouseLogo from '../assets/faux-whitelabel/atlantic-house.svg';
import schrodersLogo from '../assets/faux-whitelabel/schroders.svg';
import sansoLongchampLogo from '../assets/faux-whitelabel/sanso-longchamp.svg';
import oneLCapitalLogo from '../assets/faux-whitelabel/one-l-capital.svg';
import nisaLogo from '../assets/faux-whitelabel/nisa.svg';
import { RouteName } from '@/constants/RouteName';
import { PeriodAbbrevEnum } from '@/constants/PeriodAbbrevEnum';
import getEnv from '../utils/getEnv';
import { UserPermission } from '@/constants/UserPermission';
import { useHasPermission } from './usePermission';
import usePortfolioTree from './usePortfolioTree';
import type { IUserInfoResponse } from '@/api-v2/web/user';
import { ClientConstants } from '@/constants/ClientConstants';
import { useAdminBackdoor } from './useAdminBackdoor';

type PlatformConfig = {
  /**
   * Used for the alt text of the logo
   */
  companyName?: string;
  /**
   * Used for the variant of the navbar, defaults to primary
   */
  variant?: string;
  logo: string;
  logoStyle?: CSSProperties;
  correlationBenchmark?: string;
  defaultPeriod?: PeriodAbbrevEnum;
  /**
   * The default home page route is DATA, but we override it for some faux whitelabel teams.
   */
  homePageRoute?: RouteName;
  /**
   * When the default portfolio for a user is deleted, a new one is automatically created.
   * For some of our faux whitelabel users, we want to prevent this from happening. Mark this property `true`
   * if you'd like to do so.
   */
  shouldNotAutoCreateDefaultPortfolio?: boolean;
};

// TEAM IDs
// ============================ CORE ENV ============================
const SIMPLEX_PROD = 476;
const SIMPLEX_UAT = 125;
const BBVA_PROD = 461;
const BIA_PROD = 517;
const SOCGENCIB_PROD = 411;

/**
 * Simplex has two faux whitelabels. This is the more recent one
 * created in Dec 2024.
 */
const SIMPLEX_MULTI_CARRY_PROD = 523;
const SIMPLEX_MULTI_CARRY_UAT = 135;
const SIMPLEX_COMMODITY_MULTI_CARRY_PROD = 610;

const ATLANTIC_HOUSE_SEMI_WHITELABEL_PROD = 625;

const SCHRODERS_SEMI_WHITELABEL_PROD = 646;

const QRAFT_PROD = 494;
const WTW_PROD = 500;
const WTW_UAT = 129;
const VER_PROD = 300;
const NISA_PROD = 344;
const BRIGHTWELL_PROD = 442;
const GENERIC_FAUX_WHITELABEL_PROD = 647;
const GENERIC_FAUX_WHITELABEL_UAT = 157;

const TCORP_PROD = 472;
const TCORP_UAT = 128;
const TCORP_UAT_WALEED_TEST = 133;

/**
 * Team name is called Téthys, but the company is called Sanso-Longchamp
 */
const SANSO_LONGCHAMP_PROD = 527;

const ONE_L_CAPITAL_UAT = 134;
const ONE_L_CAPITAL_PROD = 528;

const EQUITY_CASH_TEST_PROD = 469;

const CORE_PROD_FAUX_WHITELABEL_TEAMS = [
  SIMPLEX_PROD,
  SIMPLEX_MULTI_CARRY_PROD,
  SIMPLEX_COMMODITY_MULTI_CARRY_PROD,
  QRAFT_PROD,
  WTW_PROD,
  SANSO_LONGCHAMP_PROD,
  ONE_L_CAPITAL_PROD,
  ATLANTIC_HOUSE_SEMI_WHITELABEL_PROD,
  GENERIC_FAUX_WHITELABEL_PROD,
  SCHRODERS_SEMI_WHITELABEL_PROD,
];
const CORE_UAT_FAUX_WHITELABEL_TEAMS = [
  SIMPLEX_UAT,
  SIMPLEX_MULTI_CARRY_UAT,
  WTW_UAT,
  ONE_L_CAPITAL_UAT,
  GENERIC_FAUX_WHITELABEL_UAT,
];

const CORE_PROD_TEAMS = new Set([
  ...CORE_PROD_FAUX_WHITELABEL_TEAMS,
  VER_PROD,
  NISA_PROD,
  BRIGHTWELL_PROD,
  TCORP_PROD,
  BBVA_PROD,
  BIA_PROD,
  EQUITY_CASH_TEST_PROD,
  SOCGENCIB_PROD,
]);
const CORE_UAT_TEAMS = new Set([...CORE_UAT_FAUX_WHITELABEL_TEAMS, TCORP_UAT, TCORP_UAT_WALEED_TEST]);

// ============================ BARCLAYS ENV ============================
const BARCLAYS_TEAM_UAT = 4;
const BARCLAYS_TEAM_PROD = 4;

// ============================ BOFA ENV ============================
const PREMIALAB_TEAM_BOFA_UAT_OR_SIT = 1; // SIT data is a subset of UAT data, so we use the same team ID
const PREMIALAB_TEAM_BOFA_PROD = 1;

// CONFIGS
const simplexCrossAssetConfig: PlatformConfig = {
  companyName: 'Simplex AM',
  variant: 'simplex',
  logo: simplexLogo,
  correlationBenchmark: 'NKYNTR',
  defaultPeriod: PeriodAbbrevEnum.LIVE,
  homePageRoute: RouteName.PORTFOLIO_CONSTRUCTION,
};

/**
 * Specifically does NOT have `homePageRoute`, like is defined in the original `simplexCrossAssetConfig`
 */
const simplexMultiCarryConfig: PlatformConfig = {
  companyName: 'Simplex AM',
  variant: 'simplex',
  logo: simplexLogo,
  correlationBenchmark: 'NKYNTR',
  defaultPeriod: PeriodAbbrevEnum.LIVE,
};

const simplexCommodityMultiCarryConfig: PlatformConfig = simplexCrossAssetConfig;

const atlanticHouseSemiWhitelabelConfig: PlatformConfig = {
  companyName: 'Atlantic House Semi White Label (Uncorrelated Strategies Fund)',
  variant: 'atlantic-house',
  logo: atlanticHouseLogo,
  defaultPeriod: PeriodAbbrevEnum.LIVE,
  homePageRoute: RouteName.PORTFOLIO_CONSTRUCTION,
};

const schrodersSemiWhitelabelConfig: PlatformConfig = {
  companyName: 'Schroders Semi White Label',
  variant: 'schroders',
  logo: schrodersLogo,
  defaultPeriod: PeriodAbbrevEnum.LIVE,
  homePageRoute: RouteName.PORTFOLIO_CONSTRUCTION,
};

const qraftConfig: PlatformConfig = {
  companyName: 'Qraft',
  variant: 'qraft',
  logo: qraftLogo,
  logoStyle: { 'max-height': '25px' },
  correlationBenchmark: 'SPX',
  defaultPeriod: PeriodAbbrevEnum.LIVE,
};

const wtwConfig: PlatformConfig = {
  companyName: 'Willis Towers Watson',
  variant: 'wtw',
  logo: wtwLogo,
  logoStyle: { 'max-height': '25px' },
  correlationBenchmark: 'SPX',
  defaultPeriod: PeriodAbbrevEnum.LIVE,
};

const sansoLongchampConfig: PlatformConfig = {
  companyName: 'Sanso-Longchamp',
  variant: 'sanso-longchamp',
  logo: sansoLongchampLogo,
  logoStyle: { 'max-height': '25px' },
};

const oneLCapitalConfig: PlatformConfig = {
  companyName: '1L Capital',
  variant: 'one-l-capital',
  logo: oneLCapitalLogo,
  logoStyle: { 'max-height': '50px' },
};

const genericFauxWhitelabelConfig: PlatformConfig = {
  variant: 'nisa',
  logo: nisaLogo,
  logoStyle: { 'max-height': '50px' },
};

const fauxWhitelabelConfigs: {
  [teamId: number]: PlatformConfig;
} = {
  [SIMPLEX_PROD]: simplexCrossAssetConfig,
  [SIMPLEX_UAT]: simplexCrossAssetConfig,
  [SIMPLEX_MULTI_CARRY_PROD]: simplexMultiCarryConfig,
  [SIMPLEX_COMMODITY_MULTI_CARRY_PROD]: simplexCommodityMultiCarryConfig,
  [SIMPLEX_MULTI_CARRY_UAT]: simplexMultiCarryConfig,
  [QRAFT_PROD]: qraftConfig,
  [WTW_UAT]: wtwConfig,
  [WTW_PROD]: wtwConfig,
  [SANSO_LONGCHAMP_PROD]: sansoLongchampConfig,
  [ONE_L_CAPITAL_UAT]: oneLCapitalConfig,
  [ONE_L_CAPITAL_PROD]: oneLCapitalConfig,
  [ATLANTIC_HOUSE_SEMI_WHITELABEL_PROD]: atlanticHouseSemiWhitelabelConfig,
  [SCHRODERS_SEMI_WHITELABEL_PROD]: schrodersSemiWhitelabelConfig,
  [GENERIC_FAUX_WHITELABEL_UAT]: genericFauxWhitelabelConfig,
  [GENERIC_FAUX_WHITELABEL_PROD]: genericFauxWhitelabelConfig,
};
/**
 * Feature flags layer: what experiments and in-page controls apply on a surface that already exists?
 *
 * Other layers:
 *
 * - Registration (via `getRegisteredRoutes`) answers whether a URL exists on this build.
 *
 * - Catalog (via `getNavCatalog`) is menu presentation.
 *
 * - Authorization ( via `getAreAllRouteLevelsAuthorized` / `meta.permission`) answers whether this user may open it.
 *
 * Use `supportedEnvs` / conditions here for rollouts of controls on surfaces *that
 * are already registered* — not to decide whether a page exists or is menu-visible.
 */
export function useFeatureFlag(): {
  isFauxWhitelabel: ComputedRef<boolean>;
  canSwitchPortfolios: ComputedRef<boolean>;
  fauxWhitelabelConfig: ComputedRef<PlatformConfig | null>;
  shouldShowStocksOnDiscover: ComputedRef<boolean>;
  isReadOnlyPlatform: ComputedRef<boolean>;
  isRbcPublicAccount: ComputedRef<boolean>;
  shouldPreventStrategyFactsheetAccess: ComputedRef<boolean>;
  hasHackyRedirectMap: ComputedRef<boolean>;
  hasSimplexHackyRedirectMap: ComputedRef<boolean>;
  hasBBVAHackyRedirectMap: ComputedRef<boolean>;
  hasBIAHackyRedirectMap: ComputedRef<boolean>;
  homePageRoute: ComputedRef<RouteName>;
  canSeeMyLab: ComputedRef<boolean>;
  canSeeInsights: ComputedRef<boolean>;
  userWantsToUseStrategyMask: Ref<boolean>;
  shouldUseAlgoQuant: WritableComputedRef<boolean>;
  shouldRescalePortfolioWeights: RemovableRef<boolean>;
  shouldUseClickhouse: RemovableRef<boolean>;
  canUserSeeRestrictedKnowledgeBaseArticles: ComputedRef<boolean>;
  canUserSeeApiDocumentationKBArticles: ComputedRef<boolean>;
  canUserSeeNotionKBArticles: ComputedRef<boolean>;
  canUserSeePositionKBArticles: ComputedRef<boolean>;
  hasActiveReturnAccess: ComputedRef<boolean>;
  hasAccessToConstituentRisk: ComputedRef<boolean>;
  hasAccessToStrategyPerformanceContribution: ComputedRef<boolean>;
  hasAccessToClusteringAnalysis: ComputedRef<boolean>;
  hasAccessToFactorDecomposition: ComputedRef<boolean>;
  shouldExportButtonBeVisible: ComputedRef<boolean>;
  shouldUseHomePageRoute: ComputedRef<boolean>;
  hasPortfolioConstructionAccess: ComputedRef<boolean>;
  shouldShowHSBCDisclaimer: ComputedRef<boolean>;
  canSeePremialabDisclaimer: ComputedRef<boolean>;
  canSeeDisclaimerTitle: ComputedRef<boolean>;
  shouldShowTermsAndConditionsFooter: ComputedRef<boolean>;
  canRiskPdfShowHistoryGraphs: boolean;
  canRiskPdfShowExposureVarHistoryGraphs: ComputedRef<boolean>;
  canUserSeeMarketRegimes: ComputedRef<boolean>;
  isCopyToClipboardSupported: ComputedRef<boolean>;
  shouldPerformanceContributionReturnBeVisible: ComputedRef<boolean>;
  canAccessPerformanceContributionParametersModal: ComputedRef<boolean>;
  canUserExportPerformanceContributionGraphData: ComputedRef<boolean>;
  shouldShowAdminOfficialTracks: ComputedRef<boolean>;
  shouldShowLighterBgForTables: ComputedRef<boolean>;
  hasEquityBasketAccess: ComputedRef<boolean>;
  hasQISBasketAccess: ComputedRef<boolean>;
  hasQISBasketSubmissionAccess: ComputedRef<boolean>;
  hasEquityBasketSubmissionAccess: ComputedRef<boolean>;
  hasBasketOptimizerSubmissionAccess: ComputedRef<boolean>;
  hasPeerGroupAccess: ComputedRef<boolean>;
  hasPositionPageAccess: ComputedRef<boolean>;
  showPeriodEndDateTopHoldings: ComputedRef<boolean>;
  shouldShowWhitelabelUserSettings: ComputedRef<boolean>;
  shouldShowIndexValueDisplay: ComputedRef<boolean>;
  shouldShowIndexValueTooltip: ComputedRef<boolean>;
  canSeeQuantSpaceGraph: ComputedRef<boolean>;
  shouldShowBetaFeaturesButton: RemovableRef<boolean>;
  shouldShowBetaFeatures: RemovableRef<boolean>;
  canAccessPortfolioSharingFeature: ComputedRef<boolean>;
  canSeePortfolioSaveButton: ComputedRef<boolean>;
  canSeeRegressionGlossary: ComputedRef<boolean>;
  canSeePcaGlossary: ComputedRef<boolean>;
  canSeeConstituentGlossary: ComputedRef<boolean>;
  shouldShowUnknownTermsGlossary: ComputedRef<boolean>;
  canSeeCombinedModulePdfOption: ComputedRef<boolean>;
  canSeeAiPredictionFlow: ComputedRef<boolean>;
  canUserSeeScenarioButton: ComputedRef<boolean>;
  canSeePortfolioReconciliation: ComputedRef<boolean>;
  shouldShowEquityBasketWeightingSchemes: ComputedRef<boolean>;
  shouldShowClaimedByHover: ComputedRef<boolean>;
  getIsTcorpUser: (user: Ref<IUserInfoResponse | null>) => ComputedRef<boolean>;
  canExportRiskPreferences: ComputedRef<boolean>;
  canExportRiskAdminExcel: ComputedRef<boolean>;
  shouldShowRestrictedTogglesOnBofA: ComputedRef<boolean>;
  shouldHideRiskHistoryMode: ComputedRef<boolean>;
  shouldUseLocalTimezoneForAdminConsole: RemovableRef<boolean>;
  hasAIReportPageAccess: ComputedRef<boolean>;
  hasCodeWhispererAccess: ComputedRef<boolean>;
  previewDataUniverseV3Whitelabel: RemovableRef<boolean>;
  canSeeDataUniverseV3: ComputedRef<boolean>;
  canSeeRestrictedAnalyticsToolsBofA: ComputedRef<boolean>;
  shouldShowFlagship: ComputedRef<boolean>;
  canSeePositionPageGraph: ComputedRef<boolean>;
  shouldShowRawTrack: ComputedRef<boolean>;
  shouldShowImpersonationButton: ComputedRef<boolean>;
  canSeeSmartPortfolioCreation: ComputedRef<boolean>;
  hasHistoryVersionAccess: ComputedRef<boolean>;
  hasRiskHistoryAccess: ComputedRef<boolean>;
  hasBarraFactorAccess: ComputedRef<boolean>;
  shouldShowAdminConsoleDocumentUpload: ComputedRef<boolean>;
  shouldShowAdminConsoleRiskPdfUpload: ComputedRef<boolean>;
  shouldShowAnywhereNavigation: ComputedRef<boolean>;
  isPortfolioQuickSearchDisabled: ComputedRef<boolean>;
  canSeeFxConversionOnAIReport: ComputedRef<boolean>;
  canSeeAIReportChatAndSelectedComponent: ComputedRef<boolean>;
  canSeeNewFxTypeOptions: ComputedRef<boolean>;
  shouldHideReturnIntervalDropdown: ComputedRef<boolean>;
  canSeeEquityScreener: ComputedRef<boolean>;
  canSeeOptimizerUniverseSizeCalculation: ComputedRef<boolean>;
  canUseStrategiesOnAIReport: ComputedRef<boolean>;
  canSeeAdminConsole: ComputedRef<boolean>;
  /**
   * WAA-13652: A2A nav is actually active (preference + user + not SocGen).
   * Tear-down of legacy chrome and mounting the rail both key off this.
   */
  shouldShowA2ANavigation: ComputedRef<boolean>;
  /**
   * Catalog-driven Application Navigation Sidebar (WAA-13652).
   * Super-admin A2A toggle while we validate the cutover.
   * Preference alone must not tear down legacy chrome — SocGen never mounts the rail,
   * and the rail also waits on a loaded user.
   */
  shouldShowA2ANavigationPreference: RemovableRef<boolean>;
} {
  // eslint-disable-next-line no-restricted-syntax
  const { isUserSuperAdmin, user, isUserAdmin, isUserImpersonating, hasAnyAdminRole } = useUser();

  const canSeeAdminConsole = computed(() => isUserSuperAdmin.value || isUserAdmin.value || hasAnyAdminRole.value);

  const { isSupported: isClipboardItemsSupported } = useClipboardItems();

  const {
    isCoreEnvironment,
    isHSBCEnvironment,
    isBofAEnvironment,
    isBarclaysEnvironment,
    isRBCEnvironment,
    isSocGenEnvironment,
    isProduction,
    isProdStage,
    isUatStage,
    isSitStage,
  } = getEnv();

  const getIsOnCoreProdTeam = ({ user, teamId }: { user: IUserInfoResponse | null; teamId: number }): boolean => {
    return isCoreEnvironment && isProdStage && !!user && user.teamId === teamId && CORE_PROD_TEAMS.has(teamId);
  };

  const getIsOnCoreUatTeam = ({ user, teamId }: { user: IUserInfoResponse | null; teamId: number }): boolean => {
    return isCoreEnvironment && isUatStage && !!user && user.teamId === teamId && CORE_UAT_TEAMS.has(teamId);
  };

  const getIsOnBarclaysProdTeam = ({ user, teamId }: { user: IUserInfoResponse | null; teamId: number }): boolean => {
    return isBarclaysEnvironment && isProdStage && !!user && user.teamId === teamId;
  };

  const getIsOnBarclaysUatTeam = ({ user, teamId }: { user: IUserInfoResponse | null; teamId: number }): boolean => {
    return isBarclaysEnvironment && isUatStage && !!user && user.teamId === teamId;
  };

  const isOnBofAProdPremialabAdminTeam = computed(() => {
    return isBofAEnvironment && isProdStage && user.value !== null && user.value.teamId === PREMIALAB_TEAM_BOFA_PROD;
  });

  /**
   * For feature flags, we always want SIT and UAT to be treated the same way
   * SIT is used for integration testing before UAT releases, so the same features need to be visible
   * to the premialab admin on both stages
   */
  const isOnBofAUatOrSitPremialabAdminTeam = computed(() => {
    return (
      isBofAEnvironment &&
      (isUatStage || isSitStage) &&
      user.value !== null &&
      user.value.teamId === PREMIALAB_TEAM_BOFA_UAT_OR_SIT
    );
  });

  const getIsTcorpUser = (user: Ref<IUserInfoResponse | null>): ComputedRef<boolean> => {
    return computed(
      (): boolean =>
        getIsOnCoreProdTeam({ user: user.value, teamId: TCORP_PROD }) ||
        getIsOnCoreUatTeam({ user: user.value, teamId: TCORP_UAT }) ||
        getIsOnCoreUatTeam({ user: user.value, teamId: TCORP_UAT_WALEED_TEST }) ||
        false,
    );
  };

  const fauxWhitelabelTeams = computed(() => {
    // CORE and PROD teams
    if (isCoreEnvironment && isProdStage) {
      return CORE_PROD_FAUX_WHITELABEL_TEAMS;
    }

    // CORE and UAT teams
    if (isCoreEnvironment && isUatStage) {
      return CORE_UAT_FAUX_WHITELABEL_TEAMS;
    }

    return [];
  });

  const { isReadOnlyPortfolio } = usePortfolioTree();

  const isReadOnlyPlatformTeam = useHasPermission(UserPermission.READ_ONLY_PLATFORM);

  const shouldUseAdminBackdoor = useAdminBackdoor();

  /**
   * Flag to determine if the platform is in read-only mode.
   * If the user is accessing a read-only portfolio, then the platform is in read-only mode.
   * If the user is part of a read-only platform team, then the platform is in read-only mode.
   */
  const isReadOnlyPlatform = computed(
    () => isReadOnlyPortfolio.value || (isReadOnlyPlatformTeam.value && !shouldUseAdminBackdoor.value) || false,
  );

  /**
   * The Save Portfolio Button on the Portfolio Weight Toolbar should be visible when a portfolio is marked as read-only
   * It should not be visible when the user is part of a read-only platform team
   */
  const canSeePortfolioSaveButton = computed(
    () => isReadOnlyPortfolio.value || !(isReadOnlyPlatformTeam.value && !shouldUseAdminBackdoor.value),
  );

  const isFauxWhitelabel = computed(
    () => (user.value && fauxWhitelabelTeams.value.includes(user.value.teamId)) ?? false,
  );

  /**
   * RBC public guest accounts (disclaimer / limited access) currently use the read-only platform
   * team addon. Scoped to RBC so Core read-only platform teams do not pick up public-guest UX.
   */
  const isRbcPublicAccount = computed(() => isRBCEnvironment && isReadOnlyPlatformTeam.value);

  const hasAccessToConstituentRisk = computed(() => isCoreEnvironment || isBofAEnvironment || isSocGenEnvironment);

  const shouldExportButtonBeVisible = computed(
    () => isCoreEnvironment || isBarclaysEnvironment || isBofAEnvironment || isSocGenEnvironment,
  );

  const hasAccessToClusteringAnalysis = computed(
    () =>
      isCoreEnvironment &&
      !getIsOnCoreProdTeam({ user: user.value, teamId: SIMPLEX_MULTI_CARRY_PROD }) &&
      !getIsOnCoreUatTeam({ user: user.value, teamId: SIMPLEX_MULTI_CARRY_UAT }) &&
      !getIsOnCoreProdTeam({ user: user.value, teamId: SIMPLEX_COMMODITY_MULTI_CARRY_PROD }) &&
      !getIsOnCoreProdTeam({ user: user.value, teamId: ATLANTIC_HOUSE_SEMI_WHITELABEL_PROD }) &&
      !getIsOnCoreProdTeam({ user: user.value, teamId: SCHRODERS_SEMI_WHITELABEL_PROD }),
  );

  const hasAccessToFactorDecomposition = computed(() => {
    if ((!isCoreEnvironment && !isSocGenEnvironment) || !user.value) return false;

    if (shouldUseAdminBackdoor.value) return true;

    return !isReadOnlyPlatformTeam.value;
  });

  /**
   * Negative variable because most of the time we assume we have Strategy Factsheet access
   */
  const shouldPreventStrategyFactsheetAccess = computed(() => {
    if (!user.value) return true;

    if (shouldUseAdminBackdoor.value) return false;

    return isReadOnlyPlatformTeam.value;
  });

  const fauxWhitelabelConfig = computed(() => {
    if (!user.value) return null;

    return fauxWhitelabelConfigs[user.value.teamId];
  });

  const hasSimplexHackyRedirectMap = computed(() => {
    return (
      (getIsOnCoreProdTeam({ user: user.value, teamId: SIMPLEX_PROD }) ||
        getIsOnCoreUatTeam({ user: user.value, teamId: SIMPLEX_UAT }) ||
        getIsOnCoreProdTeam({ user: user.value, teamId: SIMPLEX_MULTI_CARRY_PROD }) ||
        getIsOnCoreUatTeam({ user: user.value, teamId: SIMPLEX_MULTI_CARRY_UAT }) ||
        getIsOnCoreProdTeam({ user: user.value, teamId: SIMPLEX_COMMODITY_MULTI_CARRY_PROD })) ??
      false
    );
  });

  const hasBBVAHackyRedirectMap = computed(() => {
    return getIsOnCoreProdTeam({ user: user.value, teamId: BBVA_PROD }) ?? false;
  });

  const hasBIAHackyRedirectMap = computed(() => {
    return getIsOnCoreProdTeam({ user: user.value, teamId: BIA_PROD }) ?? false;
  });

  const hasHackyRedirectMap = computed(
    () => hasSimplexHackyRedirectMap.value || hasBBVAHackyRedirectMap.value || hasBIAHackyRedirectMap.value,
  );

  const shouldUseHomePageRoute = computed(
    (): boolean => isFauxWhitelabel.value && !!fauxWhitelabelConfig.value?.homePageRoute && !isHSBCEnvironment,
  );

  const shouldShowAdminOfficialTracks = computed(() => !isHSBCEnvironment);

  /** Team IDs that can see stocks on the discover page. */
  const DISCOVER_STOCKS_VISIBLE_TEAM_CORE_PROD: number[] = [GENERIC_FAUX_WHITELABEL_PROD, EQUITY_CASH_TEST_PROD];
  const DISCOVER_STOCKS_VISIBLE_TEAM_CORE_UAT: number[] = [GENERIC_FAUX_WHITELABEL_UAT];

  /**
   * Controls whether stocks appear in the Discover universe UI (table, scatter, spider, etc.).
   *
   * Does NOT gate equities loading off Discover: DataLayer still fetches equities when
   * `!isOnDataPage` so stock benchmarks can resolve in Analytics / factsheet / DSB
   *  Equities are kept out of `allStrategiesBy*` and resolved via
   * `allTracksBy*` / equity maps instead.
   */
  const shouldShowStocksOnDiscover = computed(
    () =>
      DISCOVER_STOCKS_VISIBLE_TEAM_CORE_PROD.some((teamId) => getIsOnCoreProdTeam({ user: user.value, teamId })) ||
      DISCOVER_STOCKS_VISIBLE_TEAM_CORE_UAT.some((teamId) => getIsOnCoreUatTeam({ user: user.value, teamId })),
  );

  const homePageRoute = computed(() => {
    if (isRbcPublicAccount.value) return RouteName.ARTICLES;

    if (isFauxWhitelabel.value && fauxWhitelabelConfig.value?.homePageRoute) {
      return fauxWhitelabelConfig.value?.homePageRoute;
    }

    return RouteName.DATA;
  });

  const userWantsToUseStrategyMask = useLocalStorage(LocalStorageEntities.USE_STRATEGY_MASK_UPDATED, true);

  const shouldUseAlgoQuantLocalStorage = useLocalStorage(LocalStorageEntities.SHOULD_USE_ALGO_QUANT, false);

  const isAlgoQuantForcedOn = computed(() => isBofAEnvironment);

  /**
   * Admin toggle consumed only by `getIsDraftEligibleForAlgoQuantPortfolioEngine` for drafts that
   * are not equity baskets / equities-only QIS. BofA and Klay Capital always have AlgoQuant
   * (no toggle); the getter is forced on and the setter is a no-op.
   */
  const shouldUseAlgoQuant = computed({
    get: () => isAlgoQuantForcedOn.value || shouldUseAlgoQuantLocalStorage.value,
    set: (value) => {
      if (isAlgoQuantForcedOn.value) return;

      shouldUseAlgoQuantLocalStorage.value = value;
    },
  });

  /**
   * When enabled (default), portfolio-trees-v2 weight and exposure are rescaled for non-leveraged portfolios.
   * Admins can toggle this off to inspect raw (unscaled) values.
   */
  const shouldRescalePortfolioWeights = useLocalStorage(LocalStorageEntities.SHOULD_RESCALE_PORTFOLIO_WEIGHTS, true);

  /**
   * Admin opt-in toggle to read linear risk metrics from ClickHouse (linear_risk_metrics_v2).
   */
  const shouldUseClickhouse = useLocalStorage(LocalStorageEntities.SHOULD_USE_CLICKHOUSE, false);

  const shouldShowA2ANavigationPreference = useLocalStorage(LocalStorageEntities.SHOULD_SHOW_A2A_NAVIGATION, false);
  const shouldShowA2ANavigation = computed(
    (): boolean => !isSocGenEnvironment && shouldShowA2ANavigationPreference.value && !!user.value,
  );

  const shouldShowBetaFeaturesButton = computed(() => isUserSuperAdmin.value);

  /**
   * The hide beta features is not needed on BofA UAT since the features on BofA are not experimental anymore
   * The features on BofA Prod are still experimental for the premialab admin and not visible to BofA admins
   */
  const shouldShowRestrictedTogglesOnBofA = computed(() => isUserSuperAdmin.value);

  /**
   * Hide risk history mode for team: BBVA_PROD, SOCGENCIB_PROD
   * Until the data is more reliable
   */
  const shouldHideRiskHistoryMode = computed(
    (): boolean =>
      getIsOnCoreProdTeam({ user: user.value, teamId: BBVA_PROD }) ||
      getIsOnCoreProdTeam({ user: user.value, teamId: SOCGENCIB_PROD }),
  );

  /**
   * Boolean designed for admins to toggle experimental features during demos, etc.
   *
   * While impersonating a user who cannot see the beta-features toggle, beta features stay off.
   * An impersonated user who can see the toggle keeps the stored preference.
   */
  const shouldShowBetaFeaturesPreference = useLocalStorage(LocalStorageEntities.SHOW_BETA_FEATURES, false);

  const shouldShowBetaFeatures = computed({
    get(): boolean {
      if (isUserImpersonating.value && !shouldShowBetaFeaturesButton.value) {
        return false;
      }

      return shouldShowBetaFeaturesPreference.value;
    },
    set(value: boolean) {
      shouldShowBetaFeaturesPreference.value = value;

      if (!value) {
        shouldShowA2ANavigationPreference.value = false;
      }
    },
  });

  /**
   * Create a flag with a default value of it being only visible to admins on the Core environment when
   * shouldShowBetaFeatures is true
   */
  const createExperimentalFeatureFlag = (
    {
      nonAdminPermission,
      supportedEnvs = [ClientConstants.PREMIALAB],
      requiredCondition,
      experimental = true,
    }: {
      /**
       * The default value of this computed is dependent on whether the user is a super admin.
       * If the feature should also be made available to certain teams or other users with a given permission type,
       * feed the supplemental permissions here, i.e., it uses a logical OR: (isUserSuperAdmin || nonAdminPermission).
       */
      nonAdminPermission?: MaybeRef<boolean>;
      /**
       * The default value of this computed will only be true if we are in the Core environment.
       * To override this, supply a different value to this parameter.
       *
       * IMPORTANT: This USED to include Core no matter what. As of May 2025, this may be overridden.
       */
      supportedEnvs?: ClientConstants[];
      /**
       * If the visibility of this feature is dependent on another condition (e.g., a toggle)
       * then supply that condition to `requiredCondition`. Then this feature will only be visible when the
       * permission values, environment values, and the additional condition are all true, i.e., it uses a logical AND:
       * (isUserSuperAdmin && isCoreEnvironment && requiredCondition).
       */
      requiredCondition?: MaybeRef<boolean>;
      /**
       * Defaults to `true`. If experimental, this feature will only be visible when the admin has set
       * `shouldShowBetaFeatures` to true.
       */
      experimental?: boolean;
    } = {
      supportedEnvs: [ClientConstants.PREMIALAB],
      experimental: true,
    },
  ): ComputedRef<boolean> => {
    return computed((): boolean => {
      const isAdminOrHasPermission = isUserSuperAdmin.value || unref(nonAdminPermission ?? false);

      const isPlatformSupported = supportedEnvs.includes(import.meta.env.VITE_CLIENT as ClientConstants);

      const defaultValue = isAdminOrHasPermission && isPlatformSupported && unref(requiredCondition ?? true);

      if (experimental && isUserSuperAdmin.value) return defaultValue && shouldShowBetaFeatures.value;

      return defaultValue;
    });
  };

  /**
   * Designed to support the roll out of features to different environments for ALL USERS
   * (i.e., not gated behind beta flags, admin permissions, or other feature specific permissions).
   *
   * Pass the desired environments to the function; this defaults to supporting just CORE.
   */
  const createFeatureFlag = (
    {
      supportedEnvs,
      requiredCondition,
    }: {
      supportedEnvs: ClientConstants[];
      requiredCondition?: MaybeRef<boolean>;
    } = {
      supportedEnvs: [ClientConstants.PREMIALAB],
    },
  ): ComputedRef<boolean> => {
    return computed((): boolean => {
      const passesEnvCheck = supportedEnvs.includes(import.meta.env.VITE_CLIENT as ClientConstants);
      const passesConditionCheck =
        requiredCondition === undefined || isUserSuperAdmin.value || unref(requiredCondition);

      return passesEnvCheck && passesConditionCheck;
    });
  };

  // Some articles should only be visible on the core platform and not on whitelabels
  const canUserSeeRestrictedKnowledgeBaseArticles = createFeatureFlag();

  const shouldShowAdminConsoleDocumentUpload = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.HSBC],
    requiredCondition: isUserSuperAdmin,
  });

  const shouldShowAdminConsoleRiskPdfUpload = createFeatureFlag({
    supportedEnvs: [ClientConstants.HSBC],
  });

  const canUserSeeApiDocumentationKBArticles = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB],
    requiredCondition: isUserSuperAdmin,
  });

  const canUserSeeNotionKBArticles = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB],
    requiredCondition: computed(() => isUserSuperAdmin.value && !isProduction),
  });

  const canUserSeePositionKBArticles = createFeatureFlag({
    supportedEnvs: [ClientConstants.BOFA],
  });

  const hasPortfolioConstructionAccess = createFeatureFlag({
    supportedEnvs: [
      ClientConstants.PREMIALAB,
      ClientConstants.BARCLAYS,
      ClientConstants.BOFA,
      ClientConstants.RBC,
      ClientConstants.SOCGEN,
    ],
    requiredCondition: useHasPermission(UserPermission.PORTFOLIO),
  });

  /**
   * Only allow teams which have Equity Basket permission to access Equity Basket on BofA
   */
  const hasEquityBasketAccess = createExperimentalFeatureFlag({
    nonAdminPermission: useHasPermission(UserPermission.EQUITY_BASKET_CREATION),
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: useHasPermission(UserPermission.EQUITY_BASKET_CREATION),
    // on BofA it is not experimental, but on Core it is
    experimental: isCoreEnvironment,
  });

  /**
   * Only allow teams which have QIS Basket permission to access QIS Basket on BofA
   */
  const hasQISBasketAccess = createExperimentalFeatureFlag({
    nonAdminPermission: useHasPermission(UserPermission.QIS_BASKET_CREATION),
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: useHasPermission(UserPermission.QIS_BASKET_CREATION),
    // on BofA it is not experimental, but on Core it is
    experimental: isCoreEnvironment,
  });

  /**
   * Only allow teams which have QIS Basket submission permissions to access QIS Basket Submission
   */
  const hasQISBasketSubmissionAccess = createExperimentalFeatureFlag({
    nonAdminPermission: useHasPermission(UserPermission.QIS_BASKET_SUBMISSION),
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: useHasPermission(UserPermission.QIS_BASKET_SUBMISSION),
    // on BofA it is not experimental, but on Core it is
    experimental: isCoreEnvironment,
  });

  /**
   * Only allow teams which have Equity Basket submission permissions to access Equity Basket Submission
   */
  const hasEquityBasketSubmissionAccess = createExperimentalFeatureFlag({
    nonAdminPermission: useHasPermission(UserPermission.EQUITY_BASKET_SUBMISSION),
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: useHasPermission(UserPermission.EQUITY_BASKET_SUBMISSION),
    // on BofA it is not experimental, but on Core it is
    experimental: isCoreEnvironment,
  });

  /**
   * Only allow teams which have Equity Basket submission permissions to access Equity Basket Submission
   */
  const hasBasketOptimizerSubmissionAccess = createExperimentalFeatureFlag({
    nonAdminPermission: useHasPermission(UserPermission.BASKET_OPTIMIZER_SUBMISSION),
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: useHasPermission(UserPermission.BASKET_OPTIMIZER_SUBMISSION),
    // on BofA it is not experimental, but on Core it is
    experimental: isCoreEnvironment,
  });

  /**
   * Flag for Peer Group access - As discussed in WAA-14026 ticket, Peer group feature/dropdown enum should be hidden
   * from all whitelabels (HSBC, BofA, Barclays) platforms for now, so will re-add a feature flag here.
   *
   * Only available on CORE.
   */
  const hasPeerGroupAccess = createFeatureFlag();

  /**
   * Flag for the AI Report Page (no code dashboard)
   */
  const hasAIReportPageAccess = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB],
    requiredCondition: computed(() => !isReadOnlyPlatformTeam.value),
  });

  /**
   * Flag for the Code Whisperer
   */
  const hasCodeWhispererAccess = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB],
    requiredCondition: useHasPermission(UserPermission.CODE_WHISPERER),
  });

  /**
   * Flag for team-based Barra Factor access
   * We are currently allowing all users to access Barra Factor on BofA environment
   */
  const hasBarraFactorAccess = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: computed(() => (isBofAEnvironment ? true : useHasPermission(UserPermission.BARRA_FACTOR).value)),
  });

  const hasAccessToStrategyPerformanceContribution = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA, ClientConstants.BARCLAYS, ClientConstants.HSBC],
    requiredCondition: useHasPermission(UserPermission.STRATEGY_PERFORMANCE),
  });

  /**
   * 24 July 2024, exposing this feature to all clients on Core only (until told otherwise)
   */
  const canSeeQuantSpaceGraph = createFeatureFlag();

  const shouldShowHSBCDisclaimer = createFeatureFlag({ supportedEnvs: [ClientConstants.HSBC] });

  /**
   * The "Disclaimer" header is only visible on Core and BofA environments
   * HSBC does not need this disclaimer title
   */
  const canSeeDisclaimerTitle = createFeatureFlag({ supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA] });

  const canSeePremialabDisclaimer = createFeatureFlag();

  const shouldShowTermsAndConditionsFooter = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.HSBC],
  });

  const canUserSeeMarketRegimes = createFeatureFlag();

  /**
   * 25 Oct 2024, exposing ScenarioButton (including BLM and Market Regime functionality) to all clients on Core only
   */
  const canUserSeeScenarioButton = createFeatureFlag();

  const showPeriodEndDateTopHoldings = createFeatureFlag({ supportedEnvs: [ClientConstants.HSBC] });

  /**
   * Verifies ClipboardItem object support in the browser used for copying to clipboard.
   * Note: Firefox does not support ClipboardItem.
   *
   * Reference: https://developer.mozilla.org/en-US/docs/Web/API/ClipboardItem/ClipboardItem
   */
  const isCopyToClipboardSupported = computed(
    () => isClipboardItemsSupported.value && !navigator.userAgent.includes('Firefox'),
  );

  /**
   * VER requires seeing these history mode graphs for VaR and Exposure, but
   * we need to clean the data for the remaining clients before we can expose these pages on the PDF to them.
   */
  const canRiskPdfShowExposureVarHistoryGraphs = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB],
    requiredCondition: computed(
      () => (isUserSuperAdmin.value || getIsOnCoreProdTeam({ user: user.value, teamId: VER_PROD })) ?? false,
    ),
  });

  const hasActiveReturnAccess = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BARCLAYS, ClientConstants.SOCGEN],
  });

  const shouldShowLighterBgForTables = createFeatureFlag({
    supportedEnvs: [ClientConstants.HSBC, ClientConstants.BARCLAYS],
  });

  /**
   * Special case for showing the user setting in the sidebar.
   * For all gated whitelabel platforms (all whitelabel envs except HSBC)
   */
  const shouldShowWhitelabelUserSettings = createFeatureFlag({
    supportedEnvs: [ClientConstants.BARCLAYS, ClientConstants.BOFA],
  });

  const shouldShowEquityBasketWeightingSchemes = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: useHasPermission(UserPermission.EQUITY_BASKET_CREATION).value,
  });

  const shouldShowClaimedByHover = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: isUatStage || isSitStage,
  });

  const canAccessPerformanceContributionParametersModal = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB],
    requiredCondition: isUserSuperAdmin,
  });
  const shouldPerformanceContributionReturnBeVisible = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BARCLAYS, ClientConstants.BOFA],
  });
  const canUserExportPerformanceContributionGraphData = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB],
    requiredCondition: isUserSuperAdmin,
  });

  /**
   * If this boolean is false, then the PortfolioQuickSearch will be disabled.
   * Instead of a dropdown, it will just show the name of the current portfolio.
   *
   * Also, the Folder icon in the ApplicationSidebar will be hidden.
   * TODO WAA-11026 - Fix access rights for Simplex team
   */
  const canSwitchPortfolios = computed(() => {
    if (!user.value) return false;

    if (shouldUseAdminBackdoor.value) return true;

    return !isReadOnlyPlatformTeam.value;
  });

  /**
   * This was created to disable the PortfolioQuickSearch in the NavigationPanel
   * Since we introduced the new DSB navigation, we need to apply the same disabled logic to the AnywhereNavigation,
   * so that the user cannot see other available portfolios from the AnywhereNavigation
   * We can rename this flag to isNavigationDsbDisabled when we take down the feature flag: shouldShowAnywhereNavigation
   */
  const isPortfolioQuickSearchDisabled = computed(() => {
    return !(canSwitchPortfolios.value && !isHSBCEnvironment);
  });

  /**
   * Feature Flag to show AnywhereNavigation instead of PortfolioQuickSearch in the NavigationSidebar
   * Simplex uses PortfolioQuickSearch
   */
  const shouldShowAnywhereNavigation = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA, ClientConstants.BARCLAYS, ClientConstants.RBC],
    requiredCondition: computed(() => !hasSimplexHackyRedirectMap.value),
  });

  /**
   * TODO WAA-11026 - Fix access rights for Simplex team
   */
  const canSeeMyLab = computed(() => {
    if (!user.value) return false;

    if (shouldUseAdminBackdoor.value) return true;

    return !isReadOnlyPlatformTeam.value;
  });

  const canSeeInsights = computed(() => {
    if (!user.value) return false;

    if (shouldUseAdminBackdoor.value) return true;

    return !isReadOnlyPlatformTeam.value;
  });

  /**
   * Index Value Display is a component used to display the index value in the top right corner of the page
   * It is currently only visible in Core, Barclays on the Track Record Chart section title
   */
  const shouldShowIndexValueDisplay = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BARCLAYS],
  });

  const shouldShowIndexValueTooltip = createFeatureFlag({
    supportedEnvs: [ClientConstants.HSBC],
  });

  /**
   * Exposing this feature to admin users on Core, Barclays, and HSBC environments
   */
  const canAccessPortfolioSharingFeature = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BARCLAYS, ClientConstants.HSBC],
    requiredCondition: computed((): boolean => {
      if (isUserAdmin.value) return true;

      if (getIsOnCoreProdTeam({ user: user.value, teamId: GENERIC_FAUX_WHITELABEL_PROD })) {
        return true;
      }

      if ((import.meta.env.VITE_CLIENT as ClientConstants) === ClientConstants.BARCLAYS) {
        return (
          (getIsOnBarclaysUatTeam({ user: user.value, teamId: BARCLAYS_TEAM_UAT }) ||
            getIsOnBarclaysProdTeam({ user: user.value, teamId: BARCLAYS_TEAM_PROD })) ??
          false
        );
      }

      return false;
    }),
  });

  const isRegressionEnabled = useHasPermission(UserPermission.REGRESSION);
  const shouldRegressionGlossaryBeVisible = computed(() => isRegressionEnabled.value || isBofAEnvironment);
  const canSeeRegressionGlossary = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: shouldRegressionGlossaryBeVisible,
  });

  const isPcaEnabled = useHasPermission(UserPermission.PCA);
  const shouldPcaGlossaryBeAvailable = computed(() => isPcaEnabled.value || isBofAEnvironment);
  const canSeePcaGlossary = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: shouldPcaGlossaryBeAvailable,
  });

  const isConstituentEnabled = useHasPermission(UserPermission.CONSTITUENT);
  const shouldConstituentGlossaryBeVisible = computed(() => isConstituentEnabled.value || isBofAEnvironment);
  const canSeeConstituentGlossary = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: shouldConstituentGlossaryBeVisible,
  });

  const shouldShowUnknownTermsGlossary = createFeatureFlag();

  const canSeeCombinedModulePdfOption = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB],
    requiredCondition: getIsTcorpUser(user),
  });

  /**
   * 08 Jan 2025, exposing this feature to all clients on Core only
   */
  const canSeeAiPredictionFlow = createFeatureFlag();

  /**
   * Only allow premialab admin/ BofA team to access Position page on BofA
   * Behavior on RBC is the same as BofA
   * On Core Environment, allow all admins to position page
   */
  const hasPositionPageAccess = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA, ClientConstants.RBC, ClientConstants.SOCGEN],
    requiredCondition: useHasPermission(UserPermission.POSITION),
  });

  const canSeePositionPageGraph = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA, ClientConstants.RBC],
    requiredCondition: useHasPermission(UserPermission.POSITION),
  });

  const canSeePortfolioReconciliation = createExperimentalFeatureFlag();

  const canExportRiskPreferences = createExperimentalFeatureFlag();

  const canExportRiskAdminExcel = computed(() => isUserSuperAdmin.value);

  const shouldUseLocalTimezoneForAdminConsole = useLocalStorage(
    LocalStorageEntities.ADMIN_CONSOLE_USE_LOCAL_TIMEZONE,
    false,
  );

  /**
   * Teams which have Equity Basket creation or QIS Basket creation permissions can see Smart Portfolio Creation
   * This also applies to copy and paste in DSB modal
   */
  const canSeeSmartPortfolioCreation = createExperimentalFeatureFlag({
    nonAdminPermission:
      useHasPermission(UserPermission.EQUITY_BASKET_CREATION) || useHasPermission(UserPermission.QIS_BASKET_CREATION),
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition:
      useHasPermission(UserPermission.EQUITY_BASKET_CREATION) || useHasPermission(UserPermission.QIS_BASKET_CREATION),
    // on BofA it is not experimental, but on Core it is
    experimental: isCoreEnvironment,
  });

  const previewDataUniverseV3Whitelabel = createExperimentalFeatureFlag({
    supportedEnvs: [ClientConstants.BARCLAYS],
    experimental: !isCoreEnvironment,
  });
  const canSeeDataUniverseV3 = computed(() => isCoreEnvironment || previewDataUniverseV3Whitelabel.value);

  /**
   * As per the email from Stefan and Christophe on 12th March 2025,
   * we will hide the following analytics tools for BofA PROD environment.
   * - basket
   * - basket list (folder)
   * - risk
   * - factor decomp
   * - perf contribution
   *
   * For BofA UAT, everything will remains the same.
   * This flag is expected to only be used for BofA App sidebar and core AnalysisToolModal.
   */
  const canSeeRestrictedAnalyticsToolsBofA = createExperimentalFeatureFlag({
    supportedEnvs: [ClientConstants.BOFA],
    requiredCondition: computed(
      (): boolean => isOnBofAProdPremialabAdminTeam.value || isOnBofAUatOrSitPremialabAdminTeam.value,
    ),
    experimental: isProdStage,
  });

  const shouldShowFlagship = createExperimentalFeatureFlag({
    supportedEnvs: [ClientConstants.BOFA],
  });

  const shouldShowRawTrack = createFeatureFlag({ supportedEnvs: [ClientConstants.BOFA] });

  const shouldShowImpersonationButton = createExperimentalFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    requiredCondition: isUserSuperAdmin,
    experimental: false,
  });

  /**
   * This feature is a beta feature on the core environment, on BofA it is now available for all users
   * Change made on 05 June 2026, as per Alexandre's request
   */
  const hasHistoryVersionAccess = createExperimentalFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB, ClientConstants.BOFA],
    nonAdminPermission: isBofAEnvironment,
    experimental: isCoreEnvironment,
  });

  /**
   * Flag for team-based Risk History access.
   * Computed directly (not via `createFeatureFlag`) so the `RISK_HISTORY` permission is always
   * enforced, including for admins, rather than being bypassed by the admin override.
   * To be converted back to using `createFeatureFlag` once the feature is fully tested by the product.
   */
  const hasRiskHistoryPermission = useHasPermission(UserPermission.RISK_HISTORY);
  const hasRiskHistoryAccess = computed((): boolean => {
    const passesEnvCheck = [ClientConstants.PREMIALAB, ClientConstants.BOFA].includes(
      import.meta.env.VITE_CLIENT as ClientConstants,
    );
    return passesEnvCheck && hasRiskHistoryPermission.value;
  });

  const canSeeAIReportChatAndSelectedComponent = createExperimentalFeatureFlag();

  const canSeeFxConversionOnAIReport = createExperimentalFeatureFlag();

  /**
   * This feature to show `Hedged With Cash Daily` and `Hedged With Cash Rebalancing` should only be exposed to clients once the maths is verified by the product.
   */
  const canSeeNewFxTypeOptions = createExperimentalFeatureFlag();

  /**
   * Return interval has limited business value for BofA and conflicts with optimizer workflows.
   * Hide it in the BofA environment and use Daily Return as the default assumption.
   */
  const shouldHideReturnIntervalDropdown = createFeatureFlag({ supportedEnvs: [ClientConstants.BOFA] });

  const canSeeEquityScreener = createFeatureFlag({
    supportedEnvs: [ClientConstants.PREMIALAB],
    requiredCondition: useHasPermission(UserPermission.EQUITY_SCREENER),
  });

  /**
   * Optimizer universe size warning / calculation — BofA super admins only for testing purpose.
   * TODO: to be removed in WAA-16201
   */
  const canSeeOptimizerUniverseSizeCalculation = computed((): boolean => isBofAEnvironment && isUserSuperAdmin.value);

  const canUseStrategiesOnAIReport = createExperimentalFeatureFlag();

  return {
    isFauxWhitelabel,
    canSwitchPortfolios,
    fauxWhitelabelConfig,
    /**
     * Showing stocks on the discover page is very heavy and should be used with caution.
     *
     * Currently, the only teams that can see this on the discover page are:
     * - Generic Faux Whitelabel team (647)
     * - Equity Cash Test team (469)
     */
    shouldShowStocksOnDiscover,
    isReadOnlyPlatform,
    isRbcPublicAccount,
    shouldPreventStrategyFactsheetAccess,
    hasHackyRedirectMap,
    hasSimplexHackyRedirectMap,
    hasBBVAHackyRedirectMap,
    hasBIAHackyRedirectMap,
    homePageRoute,
    canSeeMyLab,
    canSeeInsights,
    userWantsToUseStrategyMask,
    shouldUseAlgoQuant,
    shouldRescalePortfolioWeights,
    shouldUseClickhouse,
    canUserSeeRestrictedKnowledgeBaseArticles,
    canUserSeeApiDocumentationKBArticles,
    canUserSeeNotionKBArticles,
    canUserSeePositionKBArticles,

    /**
     * Restricts Active Return visibility to only some environments for:
     * - Factor Decomposition
     * - Factsheets
     * - Portfolio Construction
     */
    hasActiveReturnAccess,

    hasAccessToConstituentRisk,
    hasAccessToStrategyPerformanceContribution,
    hasAccessToClusteringAnalysis,
    hasAccessToFactorDecomposition,
    shouldExportButtonBeVisible,
    shouldUseHomePageRoute,
    hasPortfolioConstructionAccess,
    shouldShowHSBCDisclaimer,
    canSeePremialabDisclaimer,
    canSeeDisclaimerTitle,
    shouldShowTermsAndConditionsFooter,
    canRiskPdfShowHistoryGraphs: false,
    canRiskPdfShowExposureVarHistoryGraphs,
    canUserSeeMarketRegimes,
    isCopyToClipboardSupported,
    shouldPerformanceContributionReturnBeVisible,
    canAccessPerformanceContributionParametersModal,
    canUserExportPerformanceContributionGraphData,
    shouldShowAdminOfficialTracks,
    shouldShowLighterBgForTables,
    hasEquityBasketAccess,
    hasQISBasketAccess,
    hasQISBasketSubmissionAccess,
    hasEquityBasketSubmissionAccess,
    hasBasketOptimizerSubmissionAccess,
    hasPeerGroupAccess,
    hasPositionPageAccess,
    showPeriodEndDateTopHoldings,
    shouldShowWhitelabelUserSettings,
    shouldShowIndexValueDisplay,
    shouldShowIndexValueTooltip,
    canSeeQuantSpaceGraph,
    shouldShowBetaFeaturesButton,
    shouldShowBetaFeatures,
    canAccessPortfolioSharingFeature,
    canSeePortfolioSaveButton,
    canSeeRegressionGlossary,
    canSeePcaGlossary,
    canSeeConstituentGlossary,
    shouldShowUnknownTermsGlossary,
    canSeeCombinedModulePdfOption,
    canSeeAiPredictionFlow,
    canUserSeeScenarioButton,
    canSeePortfolioReconciliation,
    shouldShowEquityBasketWeightingSchemes,
    shouldShowClaimedByHover,
    getIsTcorpUser,
    /**
     * In order to support the re-creation of user issues stemming from user preferences on Risk,
     * we expose the option to copy their preferences to the clipboard.
     */
    canExportRiskPreferences,
    canExportRiskAdminExcel,
    shouldShowRestrictedTogglesOnBofA,
    shouldHideRiskHistoryMode,
    shouldUseLocalTimezoneForAdminConsole,
    hasAIReportPageAccess,
    hasCodeWhispererAccess,
    previewDataUniverseV3Whitelabel,
    canSeeDataUniverseV3,
    canSeeRestrictedAnalyticsToolsBofA,
    shouldShowFlagship,
    canSeePositionPageGraph,
    shouldShowRawTrack,

    shouldShowImpersonationButton,
    canSeeSmartPortfolioCreation,

    hasHistoryVersionAccess,
    hasRiskHistoryAccess,
    hasBarraFactorAccess,
    shouldShowAdminConsoleDocumentUpload,
    shouldShowAdminConsoleRiskPdfUpload,

    shouldShowAnywhereNavigation,
    isPortfolioQuickSearchDisabled,

    canSeeFxConversionOnAIReport,
    canSeeAIReportChatAndSelectedComponent,
    canSeeNewFxTypeOptions,
    shouldHideReturnIntervalDropdown,
    canSeeEquityScreener,
    canSeeOptimizerUniverseSizeCalculation,
    canUseStrategiesOnAIReport,
    canSeeAdminConsole,
    shouldShowA2ANavigation,
    shouldShowA2ANavigationPreference,
  };
}
